import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Idea } from './types';

/** Live view of `users/{uid}/ideas`, newest first. */
export function useIdeas(uid: string | undefined) {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setIdeas([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, 'users', uid, 'ideas'), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setIdeas(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Idea));
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
  }, [uid]);

  return { ideas, error, loading };
}

/**
 * Live view of a single idea, for the edit route.
 *
 * A deep link to `/idea/:id` should not have to download the whole collection first, so
 * this subscribes to the one document rather than reading through `useIdeas`.
 *
 * `idea` is `null` both before the first snapshot arrives and when the document does not
 * exist — `loading` is what distinguishes them.
 */
export function useIdea(uid: string | undefined, id: string | undefined) {
  const [idea, setIdea] = useState<Idea | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid || !id) {
      setIdea(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      doc(db, 'users', uid, 'ideas', id),
      (snap) => {
        setIdea(snap.exists() ? ({ id: snap.id, ...snap.data() } as Idea) : null);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
  }, [uid, id]);

  return { idea, error, loading };
}

/**
 * The fields this client may change, all required.
 *
 * Deliberately not `Partial<Idea>`: every field on `Idea` is optional on read, and spreading
 * an `undefined` into `updateDoc` is rejected by Firestore rather than ignored. The edit form
 * always holds a concrete value for each — `''` for a description the user cleared — so a
 * required type keeps that guarantee at the boundary.
 *
 * `images` holds Storage download URLs; the bytes are uploaded separately, before the save.
 * `transcript` and `createdAt` are not ours to touch.
 */
export interface IdeaEdits {
  title: string;
  description: string;
  tags: string[];
  links: string[];
  images: string[];
}

/**
 * Write an edit straight to Firestore.
 *
 * No Cloud Function in the path: `embedIdea` is an `onWrite` trigger on this document, so
 * saving here re-embeds the note for semantic search on its own. It content-hashes title and
 * description, so editing only tags or links costs nothing.
 *
 * `serverTimestamp()` rather than a client clock. The local snapshot briefly reports
 * `updatedAt` as null until the server confirms, which is harmless — nothing renders it yet.
 */
export async function updateIdea(uid: string, id: string, edits: IdeaEdits): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'ideas', id), {
    ...edits,
    updatedAt: serverTimestamp(),
  });
}

/**
 * A note as this client creates it.
 *
 * `transcript` is the text as typed, before any AI pass rewrote it — the field the phones
 * use for the raw capture. `recallHints` is whatever `processTranscript` extracted, carried
 * through untouched; it is absent on a note saved without AI, because an empty object would
 * read as "extracted, found nothing".
 */
export interface NewIdea {
  title: string;
  description: string;
  tags: string[];
  links: string[];
  transcript?: string;
  recallHints?: Record<string, unknown>;
}

/**
 * Create a note and return its id.
 *
 * **Every field iOS's decoder requires is always written** — `userId`, `title`,
 * `description`, `tags`, `links` and `createdAt`. `Idea(from:)` on iOS is a failable
 * initialiser that guards on all six, so a document missing any one of them does not render
 * with a gap there: it vanishes from the phone's list entirely, with no error anywhere.
 *
 * Otherwise this follows `NoteComposer.save` on Android. `images` is left unset rather than
 * `[]`, as iOS leaves it, and is attached afterwards by `attachIdeaImages`: the Storage rules
 * check that the idea document exists before they allow an upload under it, so the document
 * has to come first. Optional fields are omitted, not written as `undefined`, which Firestore
 * rejects.
 */
export async function createIdea(uid: string, idea: NewIdea): Promise<string> {
  const { transcript, recallHints, ...required } = idea;
  const ref = await addDoc(collection(db, 'users', uid, 'ideas'), {
    ...required,
    userId: uid,
    ...(transcript?.trim() ? { transcript } : {}),
    ...(recallHints ? { recallHints } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** Point a just-created note at the images uploaded under it. */
export async function attachIdeaImages(uid: string, id: string, images: string[]): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'ideas', id), { images, updatedAt: serverTimestamp() });
}

/**
 * Substring match across title, description and tags.
 *
 * Deliberately local and dumb for now. The backend already has `embedQuery` and
 * `embedIdea` for semantic search, and routing this box through them is the obvious next
 * step — but a filter that works offline over an already-loaded list is the right floor to
 * build on, and it costs nothing per keystroke.
 */
export function filterIdeas(ideas: Idea[], search: string): Idea[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return ideas;
  const terms = needle.split(/\s+/);
  return ideas.filter((idea) => {
    const haystack = [idea.title, idea.description, ...(idea.tags ?? [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    // Every word must appear somewhere — matching the Android fix that stopped treating a
    // spoken query as one long substring.
    return terms.every((t) => haystack.includes(t));
  });
}

/** Stable, human date for a Firestore timestamp that may not have landed yet. */
export function useDateFormatter() {
  return useMemo(
    () => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    []
  );
}
